"use client";

import { c as _c } from "react/compiler-runtime";
import { AmenityIcon } from "@/components/common/amenity-icon";
import { CloseBadgeIcon } from "@/components/common/close-badge-icon";
import { amenitiesInfo as amenitiesOptions, Amenity as ImportedAmenity } from "@/info/amenities";
import { cn } from "@/utils/cn";
import * as Popover from "@radix-ui/react-popover";
import { ChevronDown, Plus, Search, X } from "lucide-react";
import { Component, createContext, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Control, Path, useController, useWatch } from "react-hook-form";
import { Button } from "../rac/button";

// Extend the imported Amenity interface with additional properties
interface Amenity extends ImportedAmenity {
  isSelected?: boolean;
  score?: number;
  emoji?: string;
  description?: string;
  tags?: string[];
  isCustom?: boolean;
  isPremium?: boolean;
}
interface Project {
  id: string;
  name: string;
  amenities?: string[] | {
    code: string;
  }[];
}
type SearchMode = "contains" | "startsWith" | "fuzzy";
type LayoutMode = "grid" | "list" | "compact";
type SelectionMode = "single" | "multiple";

// Generic constraint for forms that have an amenities field
type FormWithAmenities = {
  amenities?: Amenity[] | string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};
interface AmenitiesIconGridProps<T extends FormWithAmenities = FormWithAmenities> {
  name?: keyof T;
  control: Control<T>;
  projectFieldName?: keyof T;
  label?: string;
  description?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  project?: Project;

  // Display options
  maxShown?: number;
  selectionMode?: SelectionMode;
  showCounter?: boolean;
  hideLabel?: boolean;

  // Search & filtering
  searchMode?: SearchMode;
  allowCustom?: boolean;
  customPrefix?: string;

  // Performance
  virtualizeThreshold?: number;
  debounceMs?: number;

  // Callbacks
  onSelectionChange?: (selected: Amenity[]) => void;
  onSearch?: (term: string) => void;
  onCustomCreate?: (name: string) => Amenity | Promise<Amenity>;

  // Styling
  variant?: "default" | "outlined" | "filled";
  size?: "sm" | "md" | "lg";

  // Hack for manual dirty tracking
  markFieldDirty?: (fieldName: string) => void;
}
interface AmenitiesContextValue {
  available: Amenity[];
  searchTerm: string;
  isLoading: boolean;
  error: string | null;
  addAmenity: (amenity: Amenity) => void;
  removeAmenity: (code: string) => void;
  setSearchTerm: (term: string) => void;
  clearSelection: () => void;
}
const AmenitiesContext = createContext<AmenitiesContextValue | null>(null);
const useAmenitiesContext = () => {
  const context = useContext(AmenitiesContext);
  if (!context) {
    throw new Error("useAmenitiesContext must be used within AmenitiesProvider");
  }
  return context;
};

/** Advanced debounce hook with cancel capability */
function useDebounce(value, t0) {
  const $ = _c(7);
  const delay = t0 === undefined ? 300 : t0;
  const [debouncedValue, setDebouncedValue] = useState(value);
  const timeoutRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== delay || $[1] !== value) {
    t1 = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    };
    t2 = [value, delay];
    $[0] = delay;
    $[1] = value;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const cancel = t3;
  let t4;
  if ($[5] !== debouncedValue) {
    t4 = [debouncedValue, cancel];
    $[5] = debouncedValue;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4 as const;
}

/** Fuzzy search implementation */
function fuzzyMatch(text: string, query: string): number {
  const textLower = text.toLowerCase();
  const queryLower = query.toLowerCase();
  let score = 0;
  let queryIndex = 0;
  for (let i = 0; i < textLower.length && queryIndex < queryLower.length; i++) {
    if (textLower[i] === queryLower[queryIndex]) {
      score += 1;
      queryIndex++;
    }
  }
  return queryIndex === queryLower.length ? score / text.length : 0;
}

/** Search amenities with different modes */
function searchAmenities(amenities: Amenity[], query: string, mode: SearchMode = "contains"): Amenity[] {
  if (!query.trim()) return amenities;
  const queryLower = query.toLowerCase().trim();
  switch (mode) {
    case "startsWith":
      return amenities.filter(a => a.name.toLowerCase().startsWith(queryLower) || a.tags && a.tags.some(tag => tag.toLowerCase().startsWith(queryLower)));
    case "fuzzy":
      return amenities.map(a => ({
        ...a,
        score: Math.max(fuzzyMatch(a.name, query), ...(a.tags ? a.tags.map(tag => fuzzyMatch(tag, query)) : [0]))
      })).filter(a => a.score > 0.3).sort((a, b) => b.score - a.score);
    case "contains":
    default:
      return amenities.filter(a => {
        const nameMatch = a.name && a.name.toLowerCase().includes(queryLower);
        const descMatch = a.description && a.description.toLowerCase().includes(queryLower);
        const tagMatch = Array.isArray(a.tags) && a.tags.some(tag => tag && tag.toLowerCase().includes(queryLower));
        const catMatch = a.category && a.category.toLowerCase().includes(queryLower);
        return nameMatch || descMatch || tagMatch || catMatch;
      });
  }
}

/** Create custom amenity with validation */
function createCustomAmenity(name: string, existingCodes: Set<string>, prefix: string = "custom"): Amenity {
  const sanitizedName = name.trim();
  const baseCode = `${prefix}-${sanitizedName.toLowerCase().replace(/[^a-z0-9]/g, "-")}`;
  let code = baseCode;
  let counter = 1;
  while (existingCodes.has(code)) {
    code = `${baseCode}-${counter++}`;
  }
  return {
    code,
    name: sanitizedName,
    icon: "",
    emoji: "🏷️",
    order: 999,
    importance: 0,
    type: ["custom"],
    category: "Custom",
    tags: ["custom", "user-created"],
    isCustom: true,
    description: `Custom amenity: ${sanitizedName}`
  };
}
function getProjectAmenities(projectId: string, project?: Project): string[] {
  if (projectId) return []; // should find amenities from the projectId
  if (project?.amenities?.length) {
    return project.amenities.map(amenity => {
      if (typeof amenity === "string") return amenity;
      if (typeof amenity === "object" && amenity.code) return amenity.code;
      return "";
    }).filter(Boolean);
  }
  return [];
}

/** Data fetching hook with caching */
function useAmenitiesData(projectId?: string, selectedCodes?: Set<string>) {
  const [isLoading] = useState(false); // setIsLoading removed (unused)
  const [error, setError] = useState<string | null>(null);
  const amenities = useMemo(() => {
    try {
      setError(null);
      if (selectedCodes) {
        return amenitiesOptions.map(amenity => {
          if (selectedCodes.has(amenity.code)) return {
            ...amenity,
            isSelected: true
          };
          return amenity;
        });
      }
      const pAmenities = projectId ? getProjectAmenities(projectId) : [];
      return amenitiesOptions.map(amenity_0 => {
        if (pAmenities.includes(amenity_0.code)) return {
          ...amenity_0,
          isSelected: true
        };
        return amenity_0;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load amenities");
      return amenitiesOptions;
    }
  }, [projectId, selectedCodes]);
  return {
    amenities,
    isLoading,
    error
  };
}

/** Error Boundary for the component */
class AmenitiesErrorBoundary extends Component<{
  children: React.ReactNode;
  fallback?: React.ReactNode;
}, {
  hasError: boolean;
  error?: Error;
}> {
  constructor(props: {
    children: React.ReactNode;
    fallback?: React.ReactNode;
  }) {
    super(props);
    this.state = {
      hasError: false
    };
  }
  static getDerivedStateFromError(error: Error) {
    return {
      hasError: true,
      error
    };
  }
  render() {
    if (this.state.hasError) {
      return this.props.fallback || <div className="p-4 border border-primary-200 rounded-lg bg-primary-50">
						<p className="text-primary-600 text-sm">Something went wrong loading amenities. Please try again.</p>
					</div>;
    }
    return this.props.children;
  }
}

/** Individual amenity chip with enhanced features */
interface ChipProps {
  amenity: Amenity;
  onRemove: (code: string) => void;
  variant?: "default" | "compact";
  disabled?: boolean;
}
const Chip = memo<ChipProps>(t0 => {
  const $ = _c(25);
  const {
    amenity,
    onRemove,
    variant: t1,
    disabled: t2
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const disabled = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== amenity.code || $[1] !== disabled || $[2] !== onRemove) {
    t3 = () => {
      if (!disabled) {
        onRemove(amenity.code);
      }
    };
    $[0] = amenity.code;
    $[1] = disabled;
    $[2] = onRemove;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleRemove = t3;
  let t4;
  if ($[4] !== disabled || $[5] !== variant) {
    const chipClasses = cn("inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm transition-all duration-200", {
      "bg-white border border-primary-600 text-primary-600 hover:bg-primary-200": variant === "default",
      "bg-primary-200 text-primary-600 px-2 py-0.5 text-xs": variant === "compact",
      "opacity-50 cursor-not-allowed": disabled,
      "hover:shadow-sm": !disabled
    });
    t4 = cn(chipClasses, "relative group");
    $[4] = disabled;
    $[5] = variant;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== amenity.code || $[8] !== amenity.emoji) {
    t5 = amenity.emoji ? <span className="text-sm">{amenity.emoji}</span> : <AmenityIcon code={amenity.code} />;
    $[7] = amenity.code;
    $[8] = amenity.emoji;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== amenity.name) {
    t6 = <span className="text-body">{amenity.name}</span>;
    $[10] = amenity.name;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== amenity.isPremium) {
    t7 = amenity.isPremium && <span className="text-xs bg-amber-100 text-amber-600 px-1 rounded">Premium</span>;
    $[12] = amenity.isPremium;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== amenity.name || $[15] !== disabled || $[16] !== handleRemove) {
    t8 = !disabled && <button type="button" onClick={handleRemove} className="absolute -top-2 -right-2 p-0.5 bg-white rounded-full border border-gray-300 shadow hover:bg-gray-100 transition-colors flex items-center justify-center w-5 h-5 opacity-0 group-hover:opacity-100 focus:opacity-100" aria-label={`Remove ${amenity.name}`}><CloseBadgeIcon className="w-3 h-3" /></button>;
    $[14] = amenity.name;
    $[15] = disabled;
    $[16] = handleRemove;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== amenity.description || $[19] !== t4 || $[20] !== t5 || $[21] !== t6 || $[22] !== t7 || $[23] !== t8) {
    t9 = <div className={t4} title={amenity.description}>{t5}{t6}{t7}{t8}</div>;
    $[18] = amenity.description;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
});
Chip.displayName = "AmenityChip";

/** Search input with enhanced features */
interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}
const SearchInput = memo<SearchInputProps>(t0 => {
  const $ = _c(18);
  const {
    value,
    onChange,
    onClear,
    placeholder: t1,
    disabled: t2,
    className
  } = t0;
  const placeholder = t1 === undefined ? "Search amenities..." : t1;
  const disabled = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== className) {
    t3 = cn("relative", className);
    $[0] = className;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== onChange) {
    t5 = e => onChange(e.target.value);
    $[3] = onChange;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = cn("w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg", "focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent", "disabled:bg-gray-50 disabled:cursor-not-allowed", "placeholder-gray-400 text-sm");
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== disabled || $[7] !== placeholder || $[8] !== t5 || $[9] !== value) {
    t7 = <input type="text" value={value} onChange={t5} placeholder={placeholder} disabled={disabled} className={t6} />;
    $[6] = disabled;
    $[7] = placeholder;
    $[8] = t5;
    $[9] = value;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== onClear || $[12] !== value) {
    t8 = value && <button type="button" onClick={onClear} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600" aria-label="Clear search"><X className="w-4 h-4" /></button>;
    $[11] = onClear;
    $[12] = value;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t3 || $[15] !== t7 || $[16] !== t8) {
    t9 = <div className={t3}>{t4}{t7}{t8}</div>;
    $[14] = t3;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  return t9;
});
SearchInput.displayName = "SearchInput";

/** Options list with virtualization support */
interface OptionsListProps {
  options: Amenity[];
  onSelect: (amenity: Amenity) => void;
  searchTerm: string;
  allowCustom: boolean;
  onCreateCustom?: (name: string) => void;
  virtualize?: boolean;
  maxHeight?: string;
}
const OptionsList = memo<OptionsListProps>(t0 => {
  const $ = _c(22);
  const {
    options,
    onSelect,
    searchTerm,
    allowCustom,
    onCreateCustom,
    maxHeight: t1
  } = t0;
  const maxHeight = t1 === undefined ? "300px" : t1;
  let t2;
  if ($[0] !== onCreateCustom || $[1] !== searchTerm) {
    t2 = () => {
      if (onCreateCustom && searchTerm.trim()) {
        onCreateCustom(searchTerm.trim());
      }
    };
    $[0] = onCreateCustom;
    $[1] = searchTerm;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleCustomCreate = t2;
  if (options.length === 0 && searchTerm.trim()) {
    let t3;
    if ($[3] !== searchTerm) {
      t3 = <p className="text-gray-500 text-sm mb-2">No amenities found for {searchTerm}</p>;
      $[3] = searchTerm;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== allowCustom || $[6] !== handleCustomCreate || $[7] !== searchTerm) {
      t4 = allowCustom && <button type="button" onClick={handleCustomCreate} className="inline-flex items-center gap-2 px-3 py-1 text-sm bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors"><Plus className="w-4 h-4" />Create {searchTerm}</button>;
      $[5] = allowCustom;
      $[6] = handleCustomCreate;
      $[7] = searchTerm;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    let t5;
    if ($[9] !== t3 || $[10] !== t4) {
      t5 = <div className="p-4 text-center">{t3}{t4}</div>;
      $[9] = t3;
      $[10] = t4;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    return t5;
  }
  let t3;
  if ($[12] !== maxHeight) {
    t3 = {
      maxHeight
    };
    $[12] = maxHeight;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== onSelect || $[15] !== options) {
    let t5;
    if ($[17] !== onSelect) {
      t5 = amenity => <button key={amenity.code} type="button" onClick={() => onSelect(amenity)} className={cn("w-full px-4 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none", "flex items-center gap-3 text-sm transition-colors")}>{amenity.emoji ? <span className="text-lg">{amenity.emoji}</span> : <AmenityIcon code={amenity.code} />}<div className="flex-1 min-w-0"><div className="font-medium text-gray-900">{amenity.name}</div>{amenity.description && <div className="text-xs text-gray-500 truncate">{amenity.description}</div>}</div>{amenity.isCustom && <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded">Custom</span>}{amenity.isPremium && <span className="text-xs bg-amber-100 text-amber-600 px-2 py-0.5 rounded">Premium</span>}</button>;
      $[17] = onSelect;
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    t4 = options.map(t5);
    $[14] = onSelect;
    $[15] = options;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[19] !== t3 || $[20] !== t4) {
    t5 = <div className="py-1" style={t3}>{t4}</div>;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  return t5;
});
OptionsList.displayName = "OptionsList";
const AmenitiesIconGrid = t0 => {
  const $ = _c(134);
  const {
    name: t1,
    control,
    projectFieldName: t2,
    label: t3,
    description,
    placeholder: t4,
    required: t5,
    disabled: t6,
    className,
    maxShown: t7,
    selectionMode: t8,
    showCounter: t9,
    hideLabel: t10,
    searchMode: t11,
    allowCustom: t12,
    customPrefix: t13,
    virtualizeThreshold: t14,
    debounceMs: t15,
    onSelectionChange,
    onSearch,
    onCustomCreate,
    project,
    markFieldDirty
  } = t0;
  const name = t1 === undefined ? "amenities" as keyof T : t1;
  const projectFieldName = t2 === undefined ? "project" as keyof T : t2;
  const label = t3 === undefined ? "Amenities" : t3;
  const placeholder = t4 === undefined ? "Search amenities..." : t4;
  const required = t5 === undefined ? false : t5;
  const disabled = t6 === undefined ? false : t6;
  const maxShown = t7 === undefined ? 5 : t7;
  const selectionMode = t8 === undefined ? "multiple" : t8;
  const showCounter = t9 === undefined ? true : t9;
  const hideLabel = t10 === undefined ? false : t10;
  const searchMode = t11 === undefined ? "contains" : t11;
  const allowCustom = t12 === undefined ? true : t12;
  const customPrefix = t13 === undefined ? "custom" : t13;
  const virtualizeThreshold = t14 === undefined ? 100 : t14;
  const debounceMs = t15 === undefined ? 300 : t15;
  const t16 = name as Path<T>;
  let t17;
  if ($[0] !== required) {
    t17 = required ? {
      required: "Please select at least one amenity"
    } : undefined;
    $[0] = required;
    $[1] = t17;
  } else {
    t17 = $[1];
  }
  let t18;
  if ($[2] !== control || $[3] !== t16 || $[4] !== t17) {
    t18 = {
      name: t16,
      control,
      rules: t17
    };
    $[2] = control;
    $[3] = t16;
    $[4] = t17;
    $[5] = t18;
  } else {
    t18 = $[5];
  }
  const {
    field,
    fieldState
  } = useController(t18);
  let t19;
  if ($[6] !== field.value) {
    t19 = Array.isArray(field.value) ? field.value : [];
    $[6] = field.value;
    $[7] = t19;
  } else {
    t19 = $[7];
  }
  const selectedAmenities = t19 as Amenity[];
  let t20;
  if ($[8] !== selectedAmenities) {
    t20 = new Set(selectedAmenities.map(_temp));
    $[8] = selectedAmenities;
    $[9] = t20;
  } else {
    t20 = $[9];
  }
  const selectedCodes = t20;
  const t21 = projectFieldName as Path<T>;
  let t22;
  if ($[10] !== control || $[11] !== t21) {
    t22 = {
      name: t21,
      control
    };
    $[10] = control;
    $[11] = t21;
    $[12] = t22;
  } else {
    t22 = $[12];
  }
  const watchedProject = useWatch(t22) as Project | undefined;
  const currentProject = project || watchedProject;
  const {
    amenities: globalAmenities,
    isLoading,
    error
  } = useAmenitiesData(currentProject?.id, selectedCodes);
  const hasInitialized = useRef(false);
  const hasBeenCleared = useRef(false);
  let t23;
  let t24;
  if ($[13] !== currentProject || $[14] !== field || $[15] !== globalAmenities || $[16] !== onSelectionChange || $[17] !== selectedAmenities.length) {
    t23 = () => {
      if (!hasInitialized.current && !hasBeenCleared.current && currentProject && globalAmenities.length > 0 && selectedAmenities.length === 0) {
        const projectAmenityCodes = getProjectAmenities(currentProject.id, currentProject);
        if (projectAmenityCodes.length > 0) {
          const projectAmenities = globalAmenities.filter(amenity => projectAmenityCodes.includes(amenity.code));
          if (projectAmenities.length > 0) {
            field.onChange(projectAmenities);
            onSelectionChange?.(projectAmenities);
          }
        }
        hasInitialized.current = true;
      }
    };
    t24 = [currentProject, globalAmenities, selectedAmenities.length, field, onSelectionChange];
    $[13] = currentProject;
    $[14] = field;
    $[15] = globalAmenities;
    $[16] = onSelectionChange;
    $[17] = selectedAmenities.length;
    $[18] = t23;
    $[19] = t24;
  } else {
    t23 = $[18];
    t24 = $[19];
  }
  useEffect(t23, t24);
  let t25;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => {
      hasInitialized.current = false;
      hasBeenCleared.current = false;
    };
    $[20] = t25;
  } else {
    t25 = $[20];
  }
  const t26 = currentProject?.id;
  let t27;
  if ($[21] !== t26) {
    t27 = [t26];
    $[21] = t26;
    $[22] = t27;
  } else {
    t27 = $[22];
  }
  useEffect(t25, t27);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch] = useDebounce(searchInput, debounceMs);
  let t28;
  if ($[23] !== debouncedSearch || $[24] !== globalAmenities || $[25] !== searchMode || $[26] !== selectedCodes || $[27] !== virtualizeThreshold) {
    let t29;
    if ($[29] !== selectedCodes) {
      t29 = a_0 => !selectedCodes.has(a_0.code);
      $[29] = selectedCodes;
      $[30] = t29;
    } else {
      t29 = $[30];
    }
    const filtered = searchAmenities(globalAmenities.filter(t29), debouncedSearch, searchMode);
    t28 = virtualizeThreshold && filtered.length > virtualizeThreshold ? filtered.slice(0, virtualizeThreshold) : filtered;
    $[23] = debouncedSearch;
    $[24] = globalAmenities;
    $[25] = searchMode;
    $[26] = selectedCodes;
    $[27] = virtualizeThreshold;
    $[28] = t28;
  } else {
    t28 = $[28];
  }
  const filteredOptions = t28;
  let t29;
  if ($[31] !== maxShown || $[32] !== selectedAmenities) {
    t29 = selectedAmenities.slice(0, maxShown);
    $[31] = maxShown;
    $[32] = selectedAmenities;
    $[33] = t29;
  } else {
    t29 = $[33];
  }
  const visibleChips = t29;
  const hiddenChipsCount = Math.max(0, selectedAmenities.length - maxShown);
  let t30;
  if ($[34] !== disabled || $[35] !== field || $[36] !== markFieldDirty || $[37] !== name || $[38] !== onSelectionChange || $[39] !== selectedAmenities || $[40] !== selectedCodes || $[41] !== selectionMode) {
    t30 = amenity_0 => {
      if (selectedCodes.has(amenity_0.code) || disabled) {
        return;
      }
      const newSelected = selectionMode === "single" ? [amenity_0] : [...selectedAmenities, amenity_0];
      field.onChange(newSelected);
      if (markFieldDirty && name) {
        markFieldDirty(String(name));
      }
      onSelectionChange?.(newSelected);
    };
    $[34] = disabled;
    $[35] = field;
    $[36] = markFieldDirty;
    $[37] = name;
    $[38] = onSelectionChange;
    $[39] = selectedAmenities;
    $[40] = selectedCodes;
    $[41] = selectionMode;
    $[42] = t30;
  } else {
    t30 = $[42];
  }
  const addAmenity = t30;
  let t31;
  if ($[43] !== disabled || $[44] !== field || $[45] !== markFieldDirty || $[46] !== name || $[47] !== onSelectionChange || $[48] !== selectedAmenities) {
    t31 = code => {
      if (disabled) {
        return;
      }
      const newSelected_0 = selectedAmenities.filter(a_1 => a_1.code !== code);
      field.onChange(newSelected_0);
      if (markFieldDirty && name) {
        markFieldDirty(String(name));
      }
      onSelectionChange?.(newSelected_0);
    };
    $[43] = disabled;
    $[44] = field;
    $[45] = markFieldDirty;
    $[46] = name;
    $[47] = onSelectionChange;
    $[48] = selectedAmenities;
    $[49] = t31;
  } else {
    t31 = $[49];
  }
  const removeAmenity = t31;
  let t32;
  if ($[50] !== onSearch) {
    t32 = term => {
      setSearchInput(term);
      onSearch?.(term);
    };
    $[50] = onSearch;
    $[51] = t32;
  } else {
    t32 = $[51];
  }
  const handleSearch = t32;
  let t33;
  if ($[52] !== addAmenity || $[53] !== allowCustom || $[54] !== customPrefix || $[55] !== disabled || $[56] !== onCustomCreate || $[57] !== selectedCodes) {
    t33 = name_0 => {
      if (!allowCustom || disabled) {
        return;
      }
      const customAmenity = onCustomCreate ? onCustomCreate(name_0) : createCustomAmenity(name_0, selectedCodes, customPrefix);
      if (customAmenity instanceof Promise) {
        customAmenity.then(addAmenity);
      } else {
        addAmenity(customAmenity);
      }
      setSearchInput("");
    };
    $[52] = addAmenity;
    $[53] = allowCustom;
    $[54] = customPrefix;
    $[55] = disabled;
    $[56] = onCustomCreate;
    $[57] = selectedCodes;
    $[58] = t33;
  } else {
    t33 = $[58];
  }
  const handleCustomCreate = t33;
  let t34;
  if ($[59] !== disabled || $[60] !== field || $[61] !== onSelectionChange || $[62] !== selectedAmenities.length) {
    t34 = () => {
      if (disabled || selectedAmenities.length === 0) {
        return;
      }
      hasBeenCleared.current = true;
      field.onChange([]);
      onSelectionChange?.([]);
    };
    $[59] = disabled;
    $[60] = field;
    $[61] = onSelectionChange;
    $[62] = selectedAmenities.length;
    $[63] = t34;
  } else {
    t34 = $[63];
  }
  const clearSelection = t34;
  let t35;
  if ($[64] !== globalAmenities || $[65] !== selectedCodes) {
    let t36;
    if ($[67] !== selectedCodes) {
      t36 = a_2 => !selectedCodes.has(a_2.code);
      $[67] = selectedCodes;
      $[68] = t36;
    } else {
      t36 = $[68];
    }
    t35 = globalAmenities.filter(t36);
    $[64] = globalAmenities;
    $[65] = selectedCodes;
    $[66] = t35;
  } else {
    t35 = $[66];
  }
  let t36;
  if ($[69] !== addAmenity || $[70] !== clearSelection || $[71] !== debouncedSearch || $[72] !== error || $[73] !== handleSearch || $[74] !== isLoading || $[75] !== removeAmenity || $[76] !== selectedAmenities || $[77] !== t35) {
    t36 = {
      selectedAmenities,
      available: t35,
      searchTerm: debouncedSearch,
      isLoading,
      error,
      addAmenity,
      removeAmenity,
      setSearchTerm: handleSearch,
      clearSelection
    };
    $[69] = addAmenity;
    $[70] = clearSelection;
    $[71] = debouncedSearch;
    $[72] = error;
    $[73] = handleSearch;
    $[74] = isLoading;
    $[75] = removeAmenity;
    $[76] = selectedAmenities;
    $[77] = t35;
    $[78] = t36;
  } else {
    t36 = $[78];
  }
  const contextValue = t36;
  if (error) {
    let t37;
    if ($[79] !== className) {
      t37 = cn("flex flex-col gap-2", className);
      $[79] = className;
      $[80] = t37;
    } else {
      t37 = $[80];
    }
    let t38;
    if ($[81] !== label) {
      t38 = label && <label className="text-sm font-medium text-gray-900">{label}</label>;
      $[81] = label;
      $[82] = t38;
    } else {
      t38 = $[82];
    }
    let t39;
    if ($[83] !== error) {
      t39 = <div className="p-4 border border-primary-200 rounded-lg bg-primary-50"><p className="text-primary-600 text-sm">Error: {error}</p></div>;
      $[83] = error;
      $[84] = t39;
    } else {
      t39 = $[84];
    }
    let t40;
    if ($[85] !== t37 || $[86] !== t38 || $[87] !== t39) {
      t40 = <div className={t37}>{t38}{t39}</div>;
      $[85] = t37;
      $[86] = t38;
      $[87] = t39;
      $[88] = t40;
    } else {
      t40 = $[88];
    }
    return t40;
  }
  let t37;
  if ($[89] !== className) {
    t37 = cn("flex flex-col gap-3", className);
    $[89] = className;
    $[90] = t37;
  } else {
    t37 = $[90];
  }
  let t38;
  if ($[91] !== hideLabel || $[92] !== label || $[93] !== required) {
    t38 = !hideLabel && <label className="text-title-medium">{label}{required && <span className="text-primary-500 ml-1">*</span>}</label>;
    $[91] = hideLabel;
    $[92] = label;
    $[93] = required;
    $[94] = t38;
  } else {
    t38 = $[94];
  }
  let t39;
  if ($[95] !== clearSelection || $[96] !== disabled || $[97] !== hiddenChipsCount || $[98] !== removeAmenity || $[99] !== selectedAmenities || $[100] !== visibleChips) {
    t39 = selectedAmenities.length > 0 && <div className="relative flex flex-wrap gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">{visibleChips.map(amenity_1 => <Chip key={amenity_1.code} amenity={amenity_1} onRemove={removeAmenity} disabled={disabled} />)}{hiddenChipsCount > 0 && <Popover.Root><Popover.Trigger asChild={true}><button type="button" className="inline-flex items-center gap-1 px-3 py-1 bg-white border border-primary-600 text-primary-600 rounded-full text-sm hover:bg-primary-200 transition-colors shadow group" disabled={disabled}><span className="text-body">+{hiddenChipsCount} more</span><ChevronDown className="w-3 h-3 ml-1" /></button></Popover.Trigger><Popover.Content side="bottom" align="start" className="max-w-sm max-h-64 overflow-y-auto scrollbar-thin rounded-lg border bg-white p-4 shadow-lg z-50"><div className="flex flex-wrap gap-2">{selectedAmenities.map(amenity_2 => <Chip key={amenity_2.code} amenity={amenity_2} onRemove={removeAmenity} variant="compact" disabled={disabled} />)}</div><Popover.Arrow className="fill-white" /></Popover.Content></Popover.Root>}{selectedAmenities.length > 1 && !disabled && <div className="w-full flex justify-end items-end"><Button variant="link" size="sm" onClick={clearSelection}><X className="w-4 h-4" /> Clear all</Button></div>}</div>;
    $[95] = clearSelection;
    $[96] = disabled;
    $[97] = hiddenChipsCount;
    $[98] = removeAmenity;
    $[99] = selectedAmenities;
    $[100] = visibleChips;
    $[101] = t39;
  } else {
    t39 = $[101];
  }
  let t40;
  if ($[102] !== addAmenity || $[103] !== allowCustom || $[104] !== debouncedSearch || $[105] !== disabled || $[106] !== filteredOptions || $[107] !== handleCustomCreate || $[108] !== handleSearch || $[109] !== isLoading || $[110] !== placeholder || $[111] !== searchInput) {
    t40 = isLoading ? <div className="p-8 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4" /><p className="text-sm text-gray-500">Loading amenities...</p></div> : <Popover.Root><Popover.Trigger asChild={true}><div><SearchInput value={searchInput} onChange={handleSearch} onClear={() => setSearchInput("")} placeholder={placeholder} disabled={disabled} /></div></Popover.Trigger><Popover.Content side="bottom" align="start" className="w-[var(--radix-popover-trigger-width)] max-h-80 overflow-y-auto scrollbar-thin rounded-lg border bg-white shadow-lg z-50" onOpenAutoFocus={_temp2}><OptionsList options={filteredOptions} onSelect={addAmenity} searchTerm={debouncedSearch} allowCustom={allowCustom} onCreateCustom={handleCustomCreate} maxHeight="320px" /><Popover.Arrow className="fill-white" /></Popover.Content></Popover.Root>;
    $[102] = addAmenity;
    $[103] = allowCustom;
    $[104] = debouncedSearch;
    $[105] = disabled;
    $[106] = filteredOptions;
    $[107] = handleCustomCreate;
    $[108] = handleSearch;
    $[109] = isLoading;
    $[110] = placeholder;
    $[111] = searchInput;
    $[112] = t40;
  } else {
    t40 = $[112];
  }
  let t41;
  if ($[113] !== globalAmenities.length || $[114] !== selectedAmenities.length || $[115] !== showCounter) {
    t41 = showCounter && <span>{selectedAmenities.length} selected{globalAmenities.length > 0 && <> · {globalAmenities.length - selectedAmenities.length} available</>}</span>;
    $[113] = globalAmenities.length;
    $[114] = selectedAmenities.length;
    $[115] = showCounter;
    $[116] = t41;
  } else {
    t41 = $[116];
  }
  let t42;
  if ($[117] !== t41) {
    t42 = <div className="flex items-center justify-between text-xs text-gray-500">{t41}</div>;
    $[117] = t41;
    $[118] = t42;
  } else {
    t42 = $[118];
  }
  let t43;
  if ($[119] !== description) {
    t43 = description && <p className="text-body-small">{description}</p>;
    $[119] = description;
    $[120] = t43;
  } else {
    t43 = $[120];
  }
  const t44 = fieldState.error?.message || "";
  let t45;
  if ($[121] !== t44) {
    t45 = <p className="text-body-small-medium text-destructive h-1" role="alert">{t44}</p>;
    $[121] = t44;
    $[122] = t45;
  } else {
    t45 = $[122];
  }
  let t46;
  if ($[123] !== t37 || $[124] !== t38 || $[125] !== t39 || $[126] !== t40 || $[127] !== t42 || $[128] !== t43 || $[129] !== t45) {
    t46 = <div className={t37}>{t38}{t39}{t40}{t42}{t43}{t45}</div>;
    $[123] = t37;
    $[124] = t38;
    $[125] = t39;
    $[126] = t40;
    $[127] = t42;
    $[128] = t43;
    $[129] = t45;
    $[130] = t46;
  } else {
    t46 = $[130];
  }
  let t47;
  if ($[131] !== contextValue || $[132] !== t46) {
    t47 = <AmenitiesErrorBoundary><AmenitiesContext.Provider value={contextValue}>{t46}</AmenitiesContext.Provider></AmenitiesErrorBoundary>;
    $[131] = contextValue;
    $[132] = t46;
    $[133] = t47;
  } else {
    t47 = $[133];
  }
  return t47;
};
export { AmenitiesContext, AmenitiesIconGrid, createCustomAmenity, fuzzyMatch, searchAmenities, useAmenitiesContext, type AmenitiesContextValue, type AmenitiesIconGridProps, type Amenity, type FormWithAmenities, type Amenity as ImportedAmenity, type LayoutMode, type SearchMode, type SelectionMode };
function _temp(a) {
  return a.code;
}
function _temp2(e) {
  return e.preventDefault();
}